import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PLANS, PLAN_IDS, type PlanId } from '@ready/shared';
import { useApi, type AdminOrg } from '@/lib/api';
import { ORG_TYPE_LABELS } from '@/lib/labels';
import { Badge, Button, Card, ErrorText, Field, Input, Select, UsageMeter } from '@/components/ui';
import { OrgLogo, VerifiedBadge } from '@/components/org-brand';

export function AdminOrgsPage() {
  const api = useApi();
  const [q, setQ] = useState('');
  const orgs = useQuery({ queryKey: ['admin', 'orgs', q], queryFn: () => api.admin.orgs(q) });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Input
          aria-label="Search organizations"
          placeholder="Search by name"
          className="max-w-sm"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Link to="new">
          <Button>Create organization for a customer</Button>
        </Link>
      </div>
      {orgs.isError && <ErrorText error={orgs.error} />}
      {orgs.data?.orgs.map((o) => (
        <OrgRow key={o.id} org={o} />
      ))}
    </div>
  );
}

function OrgRow({ org }: { org: AdminOrg }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [planId, setPlanId] = useState<PlanId>(org.planId);
  const [maxMembers, setMaxMembers] = useState(org.limitOverrides?.maxMembers?.toString() ?? '');
  const save = useMutation({
    mutationFn: () =>
      api.admin.updateOrg(org.id, {
        planId,
        limitOverrides: maxMembers
          ? { ...(org.limitOverrides ?? {}), maxMembers: Number(maxMembers) }
          : null,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'orgs'] }),
  });
  const archive = useMutation({
    mutationFn: () => api.admin.updateOrg(org.id, { archived: !org.archivedAt }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'orgs'] }),
  });

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <OrgLogo name={org.name} url={org.logoUrl} size="sm" />
          <Link to={`/organizations/${org.id}`} className="font-semibold hover:text-primary">
            {org.name}
          </Link>
          {org.verifiedAt && <VerifiedBadge />}
          <Badge>{ORG_TYPE_LABELS[org.type]}</Badge>
          <Badge>{org.country}</Badge>
          {org.archivedAt && <Badge>Archived</Badge>}
          {org.profileHiddenAt ? (
            <Badge>Profile hidden</Badge>
          ) : (
            org.profilePublic && <Badge>Public profile</Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Link
            to={`/o/${org.slug}`}
            className="rounded-[var(--radius)] px-3 py-2 text-sm font-medium hover:bg-muted"
          >
            Profile
          </Link>
          <Button variant="ghost" disabled={archive.isPending} onClick={() => archive.mutate()}>
            {org.archivedAt ? 'Unarchive' : 'Archive'}
          </Button>
        </div>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <UsageMeter label="Members" used={org.usage.maxMembers} limit={org.limits.maxMembers} />
        <UsageMeter
          label="Child organizations"
          used={org.usage.maxChildOrgs}
          limit={org.limits.maxChildOrgs}
        />
      </div>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <Field label="Plan" htmlFor={`op-${org.id}`}>
          <Select
            id={`op-${org.id}`}
            value={planId}
            onChange={(e) => setPlanId(e.target.value as PlanId)}
          >
            {PLAN_IDS.map((p) => (
              <option key={p} value={p}>
                {PLANS[p].name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Members override" htmlFor={`om-${org.id}`}>
          <Input
            id={`om-${org.id}`}
            type="number"
            min={1}
            placeholder="Plan default"
            value={maxMembers}
            onChange={(e) => setMaxMembers(e.target.value)}
          />
        </Field>
        <Button variant="secondary" disabled={save.isPending} onClick={() => save.mutate()}>
          Save
        </Button>
        <ErrorText error={save.error} />
      </div>
    </Card>
  );
}
